import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { forumCategories } from '@/mocks/forum';

export interface TopicRecord {
  id?: string;
  owner_id?: string;
  title: string;
  category: string;
  body: string;
}

interface TopicFormProps {
  ownerId: string;
  existing?: TopicRecord | null;
  onSaved: () => void;
}

function emptyForm(): TopicRecord {
  return {
    title: '',
    category: forumCategories[0],
    body: '',
  };
}

function TopicForm({ ownerId, existing, onSaved }: TopicFormProps) {
  const [form, setForm] = useState<TopicRecord>(() => (existing ? { ...existing } : emptyForm()));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field: keyof TopicRecord, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      setStatus('error');
      setErrorMsg('Le titre et le contenu sont obligatoires.');
      return;
    }

    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const payload = {
      title: form.title.trim(),
      category: form.category,
      body: form.body.trim(),
    };

    let error = null;
    if (existing?.id) {
      const res = await supabase.from('forum_topics').update(payload).eq('id', existing.id);
      error = res.error;
    } else {
      const res = await supabase
        .from('forum_topics')
        .insert({ ...payload, owner_id: ownerId, status: 'active' });
      error = res.error;
    }

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    onSaved();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
        <h2 className="font-heading font-semibold text-lg text-foreground-50 flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/10">
            <i className="ri-discuss-line text-secondary-400" />
          </span>
          Votre discussion
        </h2>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">
            Titre de la discussion *
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="Ex : Comment trouver ses premiers clients freelance ?"
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">Catégorie</label>
          <select
            value={form.category}
            onChange={(e) => update('category', e.target.value)}
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 outline-none focus:border-secondary-400"
          >
            {forumCategories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-400 mb-1.5">
            Contenu de la discussion *
          </label>
          <textarea
            value={form.body}
            onChange={(e) => update('body', e.target.value)}
            rows={8}
            maxLength={5000}
            placeholder="Décrivez votre question ou partagez votre expérience avec la communauté..."
            className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-secondary-400 resize-none"
          />
          <p className="mt-1 text-xs text-foreground-500">
            {form.body.length} / 5000 caractères
          </p>
        </div>
      </section>

      {status === 'error' && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
          {errorMsg}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? 'Enregistrement...' : existing?.id ? 'Mettre à jour' : 'Publier la discussion'}
        </button>
      </div>
    </form>
  );
}

export default TopicForm;